import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, BookOpen, HelpCircle, Users as UsersIcon, ArrowUp } from 'lucide-react';
import Header from './components/Header';
import ScrollProgress from './components/effects/ScrollProgress';
import AuthModal from './components/AuthModal';
import EmailVerificationModal from './components/EmailVerificationModal';
import AdminPanel from './components/AdminPanel';
import LegalPages from './components/LegalPages';
import ForumPage from './components/ForumPage';
import { LegacyHashRedirect } from './components/LegacyHashRedirect';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import NotesBrowsePage from './pages/NotesBrowsePage';
import NoteUploadPage from './pages/NoteUploadPage';
import UserAccountPage from './pages/UserAccountPage';
import CookieConsent from './components/CookieConsent';
import ForumTopBar from './components/ForumTopBar';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RatingPromptProvider } from './contexts/RatingPromptContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import ThemeWelcomeModal from './components/theme/ThemeWelcomeModal';
import ThemeSelectorModal from './components/theme/ThemeSelectorModal';
import ThemeAuthSync from './components/theme/ThemeAuthSync';
import { useSiteSettings, type SiteFooterLink } from './hooks/useApi';

const DEFAULT_FOOTER_SUBJECTS: SiteFooterLink[] = [
  { label: 'Matematik', href: '/#notes' },
  { label: 'Fizik', href: '/#notes' },
  { label: 'Kimya', href: '/#notes' },
  { label: 'Biyoloji', href: '/#notes' },
  { label: 'Türkçe', href: '/#notes' },
];

const DEFAULT_FOOTER_RESOURCES: SiteFooterLink[] = [
  { label: 'Not Yükle', href: '/notlar/yukle' },
  { label: 'Günlük Sorular', href: '/#daily-questions' },
  { label: 'Topluluk', href: '/forum' },
  { label: 'Yardım', href: '/sss' },
  { label: 'İletişim', href: 'mailto:destek@allone.com' },
];

const DEFAULT_FOOTER_SUPPORT: SiteFooterLink[] = [
  { label: 'SSS', href: '/sss' },
  { label: 'Hakkımızda', href: '/hakkimizda' },
  { label: 'Kullanım Şartları', href: '/terms' },
  { label: 'Gizlilik Politikası', href: '/privacy' },
  { label: 'Telif ve İçerik Politikası', href: '/telif' },
  { label: 'Çerez Politikası', href: '/cookies' },
];

function MeshBackground() {
  return (
    <>
      <div className="mesh-gradient" />
      <div className="mesh-blob-purple" />
      <div className="noise-overlay" />
    </>
  );
}

const footerLinkStyle: React.CSSProperties = {
  color: '#475569',
  textDecoration: 'none',
  fontSize: '14px',
  display: 'block',
  marginBottom: '12px',
  transition: 'color 0.2s ease',
};

interface SiteFooterProps {
  isMobile: boolean;
  dersler: SiteFooterLink[];
  kaynaklar: SiteFooterLink[];
  destek: SiteFooterLink[];
}

function SiteFooter({ isMobile, dersler, kaynaklar, destek }: SiteFooterProps) {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const FooterRow = ({ href, label }: SiteFooterLink) => {
    const isExternal = href.startsWith('mailto:') || href.startsWith('http://') || href.startsWith('https://');
    return (
      <motion.div whileHover={{ x: 4 }}>
        {isExternal ? (
          <a href={href} className="footer-nav-link" style={footerLinkStyle}>
            {label}
          </a>
        ) : (
          <Link to={href} className="footer-nav-link" style={footerLinkStyle}>
            {label}
          </Link>
        )}
      </motion.div>
    );
  };

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      style={{
        position: 'relative',
        zIndex: 2,
        marginTop: '40px',
      }}
    >
      <div style={{ width: '100%', overflow: 'hidden', lineHeight: 0 }}>
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" style={{ width: '100%', height: '50px', display: 'block' }}>
          <path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
            fill="rgba(15, 23, 42, 0.5)"
            opacity="0.3"
          />
          <path
            d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
            fill="rgba(15, 23, 42, 0.6)"
            opacity="0.5"
          />
        </svg>
      </div>

      <div className="site-footer__inner">
        <div style={{ maxWidth: '1300px', margin: '0 auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: isMobile ? '28px 16px' : '48px',
              marginBottom: isMobile ? '32px' : '48px',
            }}
          >
            <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <div className="site-footer__logo">
                  <span
                    style={{
                      fontSize: '10px',
                      fontWeight: '900',
                      color: 'white',
                      writingMode: 'vertical-rl' as const,
                      letterSpacing: '2px',
                    }}
                  >
                    ALL
                  </span>
                </div>
                <h3
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    color: 'white',
                    margin: 0,
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  All One Topluluğu
                </h3>
              </div>
              <p style={{ color: '#475569', fontSize: '14px', lineHeight: '1.7', marginBottom: '20px' }}>
                Üniversite öğrencileri için not paylaşımı, günlük sorular ve forum.
              </p>
              <p style={{ color: '#334155', fontSize: '13px', margin: 0 }}>
                Sosyal medya hesapları yakında eklenecek.
              </p>
            </div>

            <div>
              <h3
                style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#94a3b8',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '1.5px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <BookOpen size={14} />
                Dersler
              </h3>
              {dersler.map((link) => (
                <FooterRow key={link.label} {...link} />
              ))}
            </div>

            <div>
              <h3
                style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#94a3b8',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '1.5px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <HelpCircle size={14} />
                Kaynaklar
              </h3>
              {kaynaklar.map((link) => (
                <FooterRow key={link.label} {...link} />
              ))}
            </div>

            <div>
              <h3
                style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#94a3b8',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '1.5px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <UsersIcon size={14} />
                Destek
              </h3>
              {destek.map((link) => (
                <FooterRow key={link.label} {...link} />
              ))}
            </div>
          </div>

          <div
            style={{
              borderTop: '1px solid rgba(51, 65, 85, 0.1)',
              paddingTop: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap' as const,
              gap: '12px',
            }}
          >
            <p
              style={{
                color: '#334155',
                fontSize: isMobile ? '11px' : '13px',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                flexWrap: 'wrap' as const,
              }}
            >
              © 2026 All One Topluluğu. Tüm hakları saklıdır. Made with{' '}
              <Heart size={12} color="#ef4444" fill="#ef4444" />
            </p>

            <motion.button
              type="button"
              onClick={scrollToTop}
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Yukarı çık"
              className="site-footer__scroll-top"
              style={{
                borderRadius: '10px',
                padding: '10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ArrowUp size={16} />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.footer>
  );
}

function AuthOverlays() {
  const { showEmailVerification, user, dismissEmailVerification } = useAuth();

  return (
    <>
      <AuthModal />
      {showEmailVerification && user?.email && (
        <EmailVerificationModal email={user.email} onComplete={dismissEmailVerification} />
      )}
    </>
  );
}

function MainShell({ children }: { children: React.ReactNode }) {
  const [isMobile, setIsMobile] = useState(false);
  const { settings } = useSiteSettings();

  const dersler =
    settings?.footerSubjects && settings.footerSubjects.length > 0 ? settings.footerSubjects : DEFAULT_FOOTER_SUBJECTS;
  const kaynaklar =
    settings?.footerResources && settings.footerResources.length > 0
      ? settings.footerResources
      : DEFAULT_FOOTER_RESOURCES;
  const destek =
    settings?.footerSupport && settings.footerSupport.length > 0 ? settings.footerSupport : DEFAULT_FOOTER_SUPPORT;

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <div className="app-shell">
      <ScrollProgress />
      <AuthOverlays />
      <MeshBackground />
      <Header />
      {children}
      <SiteFooter isMobile={isMobile} dersler={dersler} kaynaklar={kaynaklar} destek={destek} />
      <CookieConsent />
    </div>
  );
}

function AdminRoute() {
  const { user, isLoggedIn, loading, openAuthModal } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!isLoggedIn) {
      sessionStorage.setItem('auth_redirect', window.location.pathname + window.location.search);
      openAuthModal('login');
      navigate('/', { replace: true });
      return;
    }
    if (user?.role !== 'admin') {
      navigate('/', { replace: true });
    }
  }, [loading, isLoggedIn, user, openAuthModal, navigate]);

  if (loading) {
    return (
      <div className="app-shell" style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', color: '#94a3b8' }}>
        Oturum doğrulanıyor…
      </div>
    );
  }

  if (!isLoggedIn || user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="app-shell">
      <ScrollProgress />
      <AuthOverlays />
      <MeshBackground />
      <Header />
      <AdminPanel onClose={() => navigate('/')} />
    </div>
  );
}

function ForumShell() {
  return (
    <div className="app-shell">
      <MeshBackground />
      <AuthOverlays />
      <ForumTopBar />
      <ForumPage />
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <RatingPromptProvider>
        <ThemeAuthSync />
        <ToastProvider>
      <ThemeWelcomeModal />
      <ThemeSelectorModal />
      <LegacyHashRedirect />
      <Routes>
        <Route path="/admin" element={<AdminRoute />} />
        <Route path="/forum/thread/:threadId" element={<ForumShell />} />
        <Route path="/forum" element={<ForumShell />} />
        <Route
          path="/ara"
          element={
            <MainShell>
              <SearchPage />
            </MainShell>
          }
        />
        <Route
          path="/notlar"
          element={
            <MainShell>
              <NotesBrowsePage />
            </MainShell>
          }
        />
        <Route
          path="/notlar/yukle"
          element={
            <MainShell>
              <NoteUploadPage />
            </MainShell>
          }
        />
        <Route
          path="/hesabim"
          element={
            <MainShell>
              <UserAccountPage />
            </MainShell>
          }
        />
        <Route
          path="/sss"
          element={
            <MainShell>
              <LegalPages page="sss" />
            </MainShell>
          }
        />
        <Route
          path="/terms"
          element={
            <MainShell>
              <LegalPages page="terms" />
            </MainShell>
          }
        />
        <Route
          path="/privacy"
          element={
            <MainShell>
              <LegalPages page="privacy" />
            </MainShell>
          }
        />
        <Route
          path="/cookies"
          element={
            <MainShell>
              <LegalPages page="cookies" />
            </MainShell>
          }
        />
        <Route
          path="/telif"
          element={
            <MainShell>
              <LegalPages page="copyright" />
            </MainShell>
          }
        />
        <Route
          path="/hakkimizda"
          element={
            <MainShell>
              <LegalPages page="about" />
            </MainShell>
          }
        />
        <Route
          path="/"
          element={
            <MainShell>
              <HomePage />
            </MainShell>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ToastProvider>
        </RatingPromptProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
